import {
  COMPANY_TYPES,
  DOCUMENT_TYPES,
  DOMICILES,
  PROVINCES,
  YES_NO_OPTIONS,
} from '../../../../core/constants/client.constants';
import { formatShareAmountEs } from '../../constitution-form/utils/format-share-amount';
import { formatEuro } from '../../constitution-form/utils/socios-display.util';
import {
  OperacionCategoria,
  OperacionSubtipo,
} from '../mock-data/operaciones.mock';

export interface OperationDetailField {
  label: string;
  value: string;
  fullWidth?: boolean;
}

export interface OperationDetailSection {
  title?: string;
  fields: OperationDetailField[];
}

export interface OperationDetailDisplayOptions {
  resolveClientName?: (id: number | string | null | undefined) => string | undefined;
  resolveSeriesName?: (id: number | string | null | undefined) => string | undefined;
}

const TIPO_AMPLIACION_OPTIONS = [
  { label: 'Dineraria', value: 'dineraria' },
  { label: 'No dineraria', value: 'no_dineraria' },
  { label: 'Régimen especial Canje de Valores', value: 'canje_valores' },
];

const MODO_AMPLIACION_OPTIONS = [
  { label: 'Ampliación VN (Valor Nominal)', value: 'ampliacion_vn' },
  { label: 'Ampliación acciones', value: 'ampliacion_acciones' },
];

const TIPO_REDUCCION_OPTIONS = [
  { label: 'Disminución VN', value: 'disminucion_vn' },
  { label: 'Amortización acciones', value: 'amortizacion_acciones' },
];

const TIPO_TRANSMISION_OPTIONS = [
  { label: 'Compraventa', value: 'compraventa' },
  { label: 'Donación', value: 'donacion' },
  { label: 'Herencia', value: 'herencia' },
  { label: 'Permuta', value: 'permuta' },
  { label: 'Régimen especial de Canje de Valores', value: 'canje_valores' },
  { label: 'Fusión', value: 'fusion' },
];

const TIPO_FUSION_OPTIONS = [
  { label: 'Sin ampliación de capital', value: 'sin_ampliacion' },
  { label: 'Con ampliación de capital', value: 'con_ampliacion' },
];

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

function resolveOptionLabel(
  options: Array<{ label: string; value: string | boolean }>,
  value: unknown,
): string {
  if (!hasValue(value)) return '';
  const found = options.find(o => o.value === value);
  return found?.label ?? String(value);
}

function parseLooseNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const s = value.trim().replace(/\s*€\s*/g, '').trim();
  if (!s) return null;
  const spanishGrouped = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/;
  const spanishDecimal = /^-?\d+,\d+$/;
  if (spanishGrouped.test(s) || spanishDecimal.test(s)) {
    const n = Number(s.replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function formatShareDisplay(value: unknown): string {
  const n = parseLooseNumber(value);
  return n == null ? String(value ?? '') : formatShareAmountEs(n);
}

function formatEuroDisplay(value: unknown): string {
  const n = parseLooseNumber(value);
  return n == null ? String(value ?? '') : formatEuro(n);
}

function formatRangos(rangos: unknown): string {
  if (!Array.isArray(rangos) || rangos.length === 0) return '';
  return rangos
    .filter((r: any) => hasValue(r?.desde) || hasValue(r?.hasta))
    .map((r: any) => `${formatShareDisplay(r.desde ?? '?')} – ${formatShareDisplay(r.hasta ?? '?')}`)
    .join(', ');
}

function formatTransmisionRangos(
  rangos: unknown,
  seriesMap: Map<number, string>,
  options?: OperationDetailDisplayOptions,
): string {
  if (!Array.isArray(rangos) || rangos.length === 0) return '';
  return rangos
    .filter((r: any) => hasValue(r?.desde) || hasValue(r?.hasta) || hasValue(r?.serie) || hasValue(r?.serieNombre))
    .map((r: any) => {
      const nums = `${formatShareDisplay(r.desde ?? '?')} – ${formatShareDisplay(r.hasta ?? '?')}`;
      const serie = resolveSeriesDisplay(r.serie, seriesMap, options, r.serieNombre);
      return serie ? `${serie}: ${nums}` : nums;
    })
    .join(', ');
}

function formatDateDisplay(value: unknown): string {
  if (!hasValue(value)) return '';
  const str = String(value);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return str;
}

function pushField(
  fields: OperationDetailField[],
  label: string,
  value: unknown,
  opts?: { fullWidth?: boolean },
): void {
  if (!hasValue(value)) return;
  fields.push({
    label,
    value: String(value),
    fullWidth: opts?.fullWidth,
  });
}

function pushEuroField(
  fields: OperationDetailField[],
  label: string,
  value: unknown,
): void {
  if (!hasValue(value)) return;
  pushField(fields, label, formatEuroDisplay(value));
}

function pushSharesField(
  fields: OperationDetailField[],
  label: string,
  value: unknown,
): void {
  if (!hasValue(value)) return;
  pushField(fields, label, formatShareDisplay(value));
}

function pushOptionField(
  fields: OperationDetailField[],
  label: string,
  options: Array<{ label: string; value: string | boolean }>,
  value: unknown,
): void {
  const resolved = resolveOptionLabel(options, value);
  pushField(fields, label, resolved);
}

function buildSeriesNameMap(operationData: Record<string, unknown>): Map<number, string> {
  const map = new Map<number, string>();
  const cap = operationData['capitalSocial'] as Record<string, unknown> | undefined;
  const series = Array.isArray(cap?.['series']) ? cap['series'] : [];
  for (const s of series) {
    const row = s as Record<string, unknown>;
    const name = row['name'] ?? row['nombre'];
    if (!hasValue(name)) continue;
    const ids = [row['id'], row['feLocalId']].filter(hasValue);
    for (const id of ids) {
      const numId = Number(id);
      if (!Number.isNaN(numId)) {
        map.set(numId, String(name));
      }
    }
  }
  return map;
}

function resolveSeriesDisplay(
  seriesId: unknown,
  seriesMap: Map<number, string>,
  options?: OperationDetailDisplayOptions,
  storedName?: unknown,
): string {
  if (hasValue(storedName)) return String(storedName);
  if (!hasValue(seriesId)) return '';
  const numId = Number(seriesId);
  if (!Number.isNaN(numId) && seriesMap.has(numId)) {
    return seriesMap.get(numId)!;
  }
  const resolved = options?.resolveSeriesName?.(
    seriesId as number | string | null | undefined,
  );
  if (resolved) return resolved;
  return String(seriesId);
}

function appendReduccionFields(
  entry: Record<string, unknown> | null | undefined,
  title: string,
  seriesMap: Map<number, string>,
  options?: OperationDetailDisplayOptions,
  tipoOptions = TIPO_REDUCCION_OPTIONS,
  tipoLabel = 'Tipo de reducción',
): OperationDetailSection | null {
  if (!entry) return null;
  const fields: OperationDetailField[] = [];
  pushOptionField(fields, tipoLabel, tipoOptions, entry['tipoReduccion'] ?? entry['tipoContrasplit']);
  pushField(
    fields,
    'Serie',
    resolveSeriesDisplay(
      entry['serie'],
      seriesMap,
      options,
      entry['serieNombre'] ?? entry['serieName'],
    ),
  );
  pushEuroField(fields, 'Importe reducido', entry['importeReducido']);
  pushEuroField(fields, 'Importe ampliado', entry['importeAmpliado']);
  pushEuroField(fields, 'Nuevo VN', entry['nuevoVN']);
  pushSharesField(fields, 'Nº acciones amortizadas', entry['numAccionesAmortizadas']);
  if (hasValue(entry['fechaBaja'])) pushField(fields, 'Fecha de baja', formatBajaDate(entry['fechaBaja']));
  const rangos = formatRangos(entry['rangos']);
  pushField(fields, 'Numeración', rangos);
  if (fields.length === 0) return null;
  return { title, fields };
}

function appendAmpliacionFields(
  data: Record<string, unknown>,
  seriesMap: Map<number, string>,
  title = 'Ampliación',
  options?: OperationDetailDisplayOptions,
): OperationDetailSection | null {
  const fields: OperationDetailField[] = [];
  pushOptionField(fields, 'Tipo de ampliación', TIPO_AMPLIACION_OPTIONS, data['tipoAmpliacion']);
  pushField(fields, 'Detalle aportaciones', data['detalleAportaciones'], { fullWidth: true });
  pushField(fields, 'Detalle canje', data['detalleCanje'], { fullWidth: true });
  pushOptionField(fields, 'Modo ampliación', MODO_AMPLIACION_OPTIONS, data['modoAmpliacion']);
  pushField(fields, 'Serie (VN)', resolveSeriesDisplay(
    data['vnSerie'],
    seriesMap,
    options,
    data['vnSerieNombre'],
  ));
  pushEuroField(fields, 'Importe ampliado (VN)', data['vnImporteAmpliado']);
  pushEuroField(fields, 'Nuevo VN', data['vnNuevoVN']);
  for (const extra of (Array.isArray(data['vnExtras']) ? data['vnExtras'] : []) as Array<Record<string, unknown>>) {
    pushField(fields, 'Serie (VN)', resolveSeriesDisplay(extra['serie'], seriesMap, options, extra['serieNombre']));
    pushEuroField(fields, 'Importe ampliado (VN)', extra['importe']);
    pushEuroField(fields, 'Nuevo VN', extra['nuevoVN']);
  }
  pushField(fields, 'Serie (acciones)', resolveSeriesDisplay(
    data['accSerie'],
    seriesMap,
    options,
    data['accSerieNombre'],
  ));
  pushSharesField(fields, 'Nuevas acciones', data['accNuevasAcciones'] ?? data['accNumAmpliado']);
  pushSharesField(fields, 'Numeración desde', data['accDesde']);
  pushSharesField(fields, 'Numeración hasta', data['accHasta']);
  for (const extra of (Array.isArray(data['accExtras']) ? data['accExtras'] : []) as Array<Record<string, unknown>>) {
    pushField(fields, 'Serie (acciones)', resolveSeriesDisplay(extra['serie'], seriesMap, options, extra['serieNombre']));
    pushSharesField(fields, 'Nuevas acciones', extra['nuevas']);
    pushSharesField(fields, 'Numeración desde', extra['desde']);
    pushSharesField(fields, 'Numeración hasta', extra['hasta']);
  }
  if (fields.length === 0) return null;
  return { title, fields };
}

function buildDomicilioSection(data: Record<string, unknown>): OperationDetailSection | null {
  const fields: OperationDetailField[] = [];
  pushOptionField(fields, 'Tipo de domicilio', DOMICILES, data['tipo']);
  pushField(fields, 'Fecha de alta', formatDateDisplay(data['fechaAlta']));
  pushField(fields, 'Siglas', data['siglas']);
  pushField(fields, 'Vía pública', data['viapublica']);
  pushField(fields, 'Número', data['numero']);
  pushField(fields, 'Escalera', data['escalera']);
  pushField(fields, 'Piso', data['piso']);
  pushField(fields, 'Puerta', data['puerta']);
  pushField(fields, 'Municipio', data['municipio']);
  pushField(fields, 'Código postal', data['cp']);
  pushOptionField(fields, 'Provincia', PROVINCES, data['provincia']);
  pushField(fields, 'País', data['pais']);
  pushField(fields, 'Ref. catastral', data['refCatastral']);
  pushField(fields, 'Observaciones', data['observaciones'], { fullWidth: true });
  if (fields.length === 0) return null;
  return { title: 'Domicilio social', fields };
}

function formatBajaDate(value: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value ?? '');
}

function buildTransmisionSections(
  data: Record<string, unknown>,
  seriesMap: Map<number, string>,
  options?: OperationDetailDisplayOptions,
): OperationDetailSection[] {
  const resolveName = (id: unknown) =>
    options?.resolveClientName?.(id as number | string) ?? id;
  const adquirentes = Array.isArray(data['adquirentes'])
    ? (data['adquirentes'] as Record<string, unknown>[])
    : [];
  const sections: OperationDetailSection[] = [];

  const fields: OperationDetailField[] = [];
  pushOptionField(fields, 'Tipo de transmisión', TIPO_TRANSMISION_OPTIONS, data['tipoTransmision']);
  pushField(fields, 'Transmitente', resolveName(data['transmitente']));
  const pushAdquirenteFields = (target: OperationDetailField[], a: Record<string, unknown>) => {
    pushField(target, 'Adquirente', resolveName(a['adquirente']));
    pushSharesField(target, 'Nº acciones vendidas', a['numAccionesVendidas']);
    pushField(target, 'Numeración', formatTransmisionRangos(a['numeracionAccionesVendidas'], seriesMap, options));
    pushEuroField(target, 'Prima de emisión', a['primaEmision']);
    pushEuroField(target, 'Precio transmisión', a['precioTransmision']);
  };

  if (adquirentes.length === 0) {
    // operations saved before several adquirentes were supported
    pushAdquirenteFields(fields, data);
  }
  if (fields.length > 0) sections.push({ title: 'Transmisión de acciones', fields });

  adquirentes.forEach((a, idx) => {
    const adqFields: OperationDetailField[] = [];
    pushAdquirenteFields(adqFields, a);
    if (adqFields.length > 0) {
      sections.push({ title: adquirentes.length > 1 ? `Adquirente ${idx + 1}` : 'Adquirente', fields: adqFields });
    }
  });

  const bajas = Array.isArray(data['bajasParticipaciones'])
    ? (data['bajasParticipaciones'] as Record<string, unknown>[])
    : [];
  if (bajas.length > 0) {
    const lines = bajas.map(b => {
      const serie = hasValue(b['serieNombre']) ? `${b['serieNombre']}: ` : '';
      return `${serie}${formatShareDisplay(b['desde'])} – ${formatShareDisplay(b['hasta'])} (baja ${formatBajaDate(b['fechaBaja'])})`;
    });
    sections.push({
      title: 'Participaciones dadas de baja',
      fields: [{ label: 'Participaciones', value: lines.join('\n'), fullWidth: true }],
    });
  }
  return sections;
}

function buildTransformacionSection(data: Record<string, unknown>): OperationDetailSection | null {
  const fields: OperationDetailField[] = [];
  pushField(fields, 'Razón social', data['name']);
  pushField(fields, 'Código Cliente', data['code']);
  pushField(fields, 'Código aplicación', data['codeApplication']);
  pushOptionField(fields, 'Tipo de sociedad', COMPANY_TYPES, data['companyType']);
  pushOptionField(fields, 'Certificado de residencia', YES_NO_OPTIONS, data['residenceCertificate']);

  const docs = Array.isArray(data['documents']) ? data['documents'] : [];
  if (docs.length > 0) {
    const docLines = docs
      .filter((d: any) => hasValue(d?.type) || hasValue(d?.number))
      .map((d: any) => {
        const typeLabel = resolveOptionLabel(DOCUMENT_TYPES, d.type);
        const parts = [typeLabel, d.number].filter(hasValue);
        const principal = d.principal ? ' (principal)' : '';
        return parts.join(' — ') + principal;
      });
    pushField(fields, 'Documentos', docLines.join('\n'), { fullWidth: true });
  }

  if (fields.length === 0) return null;
  return { title: 'Transformación', fields };
}

export function buildOperationDetailSections(
  _categoria: OperacionCategoria,
  subtipo: OperacionSubtipo | null,
  operationData: Record<string, unknown> | null | undefined,
  options?: OperationDetailDisplayOptions,
): OperationDetailSection[] {
  if (!operationData || !subtipo) return [];

  const sections: OperationDetailSection[] = [];
  const seriesMap = buildSeriesNameMap(operationData);

  switch (subtipo) {
    case 'ampliacion_capital': {
      const section = appendAmpliacionFields(operationData, seriesMap, 'Ampliación', options);
      if (section) sections.push(section);
      break;
    }

    case 'reduccion_capital':
    case 'escision': {
      const entries = Array.isArray(operationData['reducciones'])
        ? (operationData['reducciones'] as Record<string, unknown>[])
        : [];
      const multiple = entries.length > 1;
      entries.forEach((entry, idx) => {
        const title = multiple
          ? `Reducción de capital (${idx + 1})`
          : 'Reducción de capital';
        const section = appendReduccionFields(entry, title, seriesMap, options);
        if (section) sections.push(section);
      });
      break;
    }

    case 'operacion_acordeon': {
      const reduccion = appendReduccionFields(
        operationData['reduccion'] as Record<string, unknown>,
        'Reducción',
        seriesMap,
        options,
      );
      if (reduccion) sections.push(reduccion);
      const ampliacion = appendAmpliacionFields(operationData, seriesMap, 'Ampliación', options);
      if (ampliacion) sections.push(ampliacion);
      break;
    }

    case 'contrasplit': {
      const section = appendReduccionFields(
        operationData['contrasplit'] as Record<string, unknown>,
        'Contrasplit',
        seriesMap,
        options,
        TIPO_REDUCCION_OPTIONS,
        'Tipo de contrasplit',
      );
      if (section) sections.push(section);
      break;
    }

    case 'fusion_absorbente': {
      const fusionFields: OperationDetailField[] = [];
      pushOptionField(fusionFields, 'Tipo de fusión', TIPO_FUSION_OPTIONS, operationData['tipoFusion']);
      if (fusionFields.length > 0) {
        sections.push({ title: 'Fusión', fields: fusionFields });
      }
      const ampliacion = operationData['ampliacion'] as Record<string, unknown> | undefined;
      if (ampliacion) {
        const ampSection = appendAmpliacionFields(ampliacion, seriesMap, 'Ampliación de capital', options);
        if (ampSection) sections.push(ampSection);
      }
      break;
    }

    case 'compraventa_donacion_herencia':
    case 'fusion_socios_filial': {
      sections.push(...buildTransmisionSections(operationData, seriesMap, options));
      break;
    }

    case 'denominacion_social': {
      const fields: OperationDetailField[] = [];
      pushField(fields, 'Razón social', operationData['newName']);
      const prev = operationData['previousName'];
      const next = operationData['newName'];
      if (hasValue(prev) && hasValue(next) && String(prev).trim() !== String(next).trim()) {
        pushField(fields, 'Denominación anterior', prev);
      }
      if (fields.length > 0) sections.push({ title: 'Razón social', fields });
      break;
    }

    case 'domicilio_social': {
      const section = buildDomicilioSection(operationData);
      if (section) sections.push(section);
      break;
    }

    case 'articulado_estatutos': {
      const fields: OperationDetailField[] = [];
      pushField(fields, 'Artículos modificados', operationData['articulosModificados'], { fullWidth: true });
      pushField(fields, 'Descripción del cambio', operationData['descripcionCambio'], { fullWidth: true });
      if (fields.length > 0) sections.push({ title: 'Articulado estatutos', fields });
      break;
    }

    case 'cambio_forma_juridica': {
      const section = buildTransformacionSection(operationData);
      if (section) sections.push(section);
      break;
    }

    default:
      break;
  }

  return sections;
}
